import { Check, Clear } from '@mui/icons-material';
import { Stack, StackProps, Tooltip, Typography, TypographyProps } from '@mui/material';
import { ReactNode, useMemo } from 'react';
import { dataTableHighlight } from '../DataTable';

export interface ObjectFieldItem {
  key: string;
  value: string | number | boolean | React.JSX.Element;
  tooltip?: ReactNode;
}

/** Props passed to the useWrapper callback. */
export interface ObjectFieldWrapperProps extends StackProps {
  children: ReactNode;
}

const DefaultWrapper = ({ children, ...props }: ObjectFieldWrapperProps): React.JSX.Element => (
  <Stack paddingBottom={'8px'} {...props}>
    {children}
  </Stack>
);

interface ObjectFieldProps {
  fields: ObjectFieldItem[];
  direction?: 'row' | 'column';
  // draw the field key above the value
  headlined?: boolean;
  maxWidth?: StackProps['maxWidth'];
  keyColor?: TypographyProps['color'];
  searchText?: string | string[];
  /**
   * Custom wrapper component for the outer container. Receives spacing,
   * direction, and maxWidth as StackProps. Defaults to a plain Stack with
   * bottom padding. Use this to for example inject a ScrollableStack to
   * control scroll behavior.
   */
  useWrapper?: (props: ObjectFieldWrapperProps) => React.JSX.Element;
  /** Additional inline styles applied to each field entry. */
  extraEntryStyle?: React.CSSProperties;
}

const convertValueToElement = (
  value: ObjectFieldItem['value'],
  searchText: ObjectFieldProps['searchText'],
): React.JSX.Element => {
  switch (typeof value) {
    case 'boolean':
      return value ? <Check /> : <Clear />;
    case 'string':
    case 'number':
      return (
        <Typography color={'textPrimary'} noWrap>
          {dataTableHighlight(String(value), searchText)}
        </Typography>
      );
    default:
      return value;
  }
};

export default function ObjectField({
  fields,
  direction,
  headlined,
  maxWidth,
  keyColor,
  searchText,
  useWrapper: Wrapper = DefaultWrapper,
  extraEntryStyle,
}: ObjectFieldProps): React.JSX.Element {
  const isHeadlined = !!headlined;

  const entries = useMemo(
    () =>
      fields.map((field, index) => {
        const { key, value, tooltip } = field;
        const valueElement = convertValueToElement(value, searchText);

        const labeledElement = (
          <Stack
            key={index + '_element'}
            direction={isHeadlined ? 'column' : 'row'}
            spacing={isHeadlined ? 0 : 1}
            style={extraEntryStyle}
          >
            <Typography color={keyColor ?? 'textSecondary'} fontWeight={700} noWrap>
              {key + (isHeadlined ? '' : ':')}
            </Typography>
            {valueElement}
          </Stack>
        );

        return tooltip !== undefined ? (
          <Tooltip key={index + '_tooltip'} title={tooltip}>
            {labeledElement}
          </Tooltip>
        ) : (
          labeledElement
        );
      }),
    [fields, isHeadlined, keyColor, searchText],
  );

  const useDirection = useMemo(() => {
    if (direction !== undefined) {
      return direction;
    }
    // Headlined fields and small lists render horizontally.
    // Larger non-headlined lists switch to a vertical layout.
    if (isHeadlined || fields.length < 4) {
      return 'row';
    }
    return 'column';
  }, [direction, isHeadlined, fields]);

  /**
   * Adjusts spacing for the actually used direction of the stack
   */
  const useSpacing = useMemo(() => (useDirection === 'row' ? (isHeadlined ? 2 : 1) : 0), [useDirection, isHeadlined]);

  return (
    <Wrapper spacing={useSpacing} direction={useDirection} maxWidth={maxWidth}>
      {entries}
    </Wrapper>
  );
}
